import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCopy, faDownload, faCheck, faExchangeAlt } from '@fortawesome/free-solid-svg-icons';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background-color: var(--surface-color);
  color: var(--text-color);
  padding: var(--space-6);
  border-radius: var(--radius-xl);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: var(--shadow-xl);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--border-color-light);
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--error-50);
    color: var(--error-600);
    border-color: var(--error-300);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const Title = styled.h2`
  margin-bottom: var(--space-6);
  text-align: center;
  font-size: var(--text-xl);
  color: var(--primary-color);
  font-weight: 500;
  font-family: var(--font-heading-bold);
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 10px 20px;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--surface-color)'};
  color: ${props => props.active ? 'white' : 'var(--text-color)'};
  border: 2px solid var(--primary-color);
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color-dark)' : 'var(--primary-color-light)'};
    color: white;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const ActionButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 15px;
  margin-left: 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: bold;

  &:hover {
    background-color: var(--primary-color-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  svg {
    margin-right: 8px;
  }
`;

const LogContent = styled.pre`
  background-color: var(--background-color);
  color: var(--text-color);
  padding: 20px;
  border-radius: 8px;
  font-size: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const Notification = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: var(--primary-color);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease-in-out;
  opacity: ${props => props.show ? 1 : 0};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(20px)'};
`;

const LogModal = ({ isOpen, onClose, logEntries, settings, apiLogs }) => {
  const [notification, setNotification] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'api'

  if (!isOpen) return null;

  const formatChatLog = () => {
    const log = {
      model: settings.model,
      max_tokens: settings.maxTokens,
      temperature: settings.temperature,
      system: settings.systemPrompt,
      messages: logEntries.map(entry => ({
        role: entry.role,
        content: [
          {
            type: "text",
            text: entry.content
          }
        ]
      }))
    };
    return JSON.stringify(log, null, 2);
  };

  const formatApiLog = () => {
    return apiLogs.map((log, index) => {
      return `Request ${index + 1}:\n${JSON.stringify(log.request, null, 2)}\n\nResponse ${index + 1}:\n${JSON.stringify(log.response, null, 2)}\n\n`;
    }).join('---\n\n');
  };

  const formattedLog = activeTab === 'chat' ? formatChatLog() : formatApiLog();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedLog);
      setIsCopied(true);
      setNotification('Log copied to clipboard');
      setTimeout(() => {
        setIsCopied(false);
        setNotification('');
      }, 3000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      setNotification('Failed to copy log');
    }
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([formattedLog], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = activeTab === 'chat' ? 'chat_log.json' : 'api_log.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setNotification('Log downloaded successfully');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      console.error('Failed to download: ', err);
      setNotification('Failed to download log');
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <Title>AI Chat Log</Title>
        <TabContainer>
          <Tab active={activeTab === 'chat'} onClick={() => setActiveTab('chat')}>Chat Log</Tab>
          <Tab active={activeTab === 'api'} onClick={() => setActiveTab('api')}>API Log</Tab>
        </TabContainer>
        <ButtonContainer>
          <ActionButton onClick={handleCopy}>
            <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} /> {isCopied ? 'Copied' : 'Copy'}
          </ActionButton>
          <ActionButton onClick={handleDownload}>
            <FontAwesomeIcon icon={faDownload} /> Download JSON
          </ActionButton>
        </ButtonContainer>
        <LogContent>{formattedLog}</LogContent>
      </ModalContent>
      <Notification show={!!notification}>{notification}</Notification>
    </ModalOverlay>
  );
};

export default LogModal;